import { PropTypes } from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import {
  fieldRequired,
  Input,
  requiredDatePicker,
  requiredInput,
  requiredSelect,
} from '../../../core/formValidation';
import { toString as enumToString } from '../../../core/utils';
import { isNumber } from '../../../utils/number-util';
import { UtilityType } from '../../expenses/models';
import { selectActiveUtilities } from '../../settings/reducer';
import { selectCurrentUtility } from '../utilityReducer';

const UtilityEditForm = ({
  change,
  utilityUnits,
  utilityType,
  activeUtilities,
  currentUtility,
  intl,
}) => {
  const utilityTypeOptions = Object.keys(activeUtilities)
    .filter((u) => activeUtilities[u])
    .map((type) => (
      <option key={type} value={UtilityType[type]}>
        {intl.formatMessage({ id: type })}
      </option>
    ));

  let utilityUnitOptions;
  if (isNumber(utilityType)) {
    const currentUtilityType = enumToString(UtilityType, utilityType);
    const currentUtilityUnits = utilityUnits[currentUtilityType];

    utilityUnitOptions = Object.keys(currentUtilityUnits || {}).map((unit) => (
      <option key={unit} value={currentUtilityUnits[unit]}>
        {unit}
      </option>
    ));
  }

  const typeChangedHandler = () => {
    change('unit', '0');
  };

  return (
    <>
      <div className="form-row full no-padding">
        <div className="form-group">
          <label htmlFor="type">{intl.formatMessage({ id: 'type' })}</label>
          <Field
            name="type"
            id="type"
            component={requiredSelect}
            className="form-control"
            onChange={typeChangedHandler}
            parse={(value) => Number(value)}
            value={UtilityType[currentUtility]}
            validate={[fieldRequired]}
          >
            {utilityTypeOptions}
          </Field>
        </div>
      </div>
      <div className="form-row full no-padding">
        <div className="form-group">
          <label htmlFor="name">{intl.formatMessage({ id: 'description' })}</label>
          <Field
            name="name"
            id="name"
            component={requiredInput}
            type="text"
            className="form-control"
            placeholder={intl.formatMessage({ id: 'utilityNamePlaceholder' })}
            validate={[fieldRequired]}
          />
        </div>
      </div>
      <div className="form-row half no-padding">
        <div className="form-group">
          <label htmlFor="startDate" className="mr-2">
            {intl.formatMessage({ id: 'startDate' })}
          </label>
          <Field
            name="start"
            id="start"
            component={requiredDatePicker}
            type="date"
            className="form-control"
            placeholderText={intl.formatMessage({ id: 'utilityStartDatePlaceholder' })}
            parse={(value) => value.format('YYYY-MM-DD')}
            validate={[fieldRequired]}
          />
        </div>
        <div className="form-group">
          <label htmlFor="endDate" className="mr-2">
            {intl.formatMessage({ id: 'endDate' })}
          </label>
          <Field
            name="end"
            id="end"
            component={requiredDatePicker}
            type="date"
            className="form-control"
            placeholderText={intl.formatMessage({ id: 'utilityEndDatePlaceholder' })}
            parse={(value) => value.format('YYYY-MM-DD')}
            validate={[fieldRequired]}
          />
        </div>
      </div>
      <div className="form-row half no-padding">
        <div className="form-group">
          <label htmlFor="startReading">{intl.formatMessage({ id: 'startReading' })}</label>
          <Field
            name="startReading"
            id="startReading"
            component={requiredInput}
            type="text"
            decimal="4"
            commas="true"
            className="form-control"
            placeholder={intl.formatMessage({ id: 'utilityReadingPlaceholder' })}
            validate={[fieldRequired]}
          />
        </div>
        <div className="form-group">
          <label htmlFor="endReading">{intl.formatMessage({ id: 'endReading' })}</label>
          <Field
            name="endReading"
            id="endReading"
            component={requiredInput}
            type="text"
            decimal="4"
            commas="true"
            className="form-control"
            placeholder={intl.formatMessage({ id: 'utilityReadingPlaceholder' })}
            validate={[fieldRequired]}
          />
        </div>
      </div>
      <div className="form-row full no-padding">
        <div className="form-group">
          <label htmlFor="unit">{intl.formatMessage({ id: 'readingUnit' })}</label>
          <Field
            name="unit"
            id="unit"
            component={requiredSelect}
            className="form-control"
            parse={(value) => Number(value)}
            validate={[fieldRequired]}
          >
            {utilityUnitOptions}
          </Field>
        </div>
      </div>
      <div className="form-row half no-padding">
        <div className="form-group">
          <label htmlFor="cost">{intl.formatMessage({ id: 'amount' })}</label>
          <Field
            name="cost"
            id="cost"
            component={requiredInput}
            type="text"
            decimal="2"
            commas="true"
            className="form-control"
            placeholder={intl.formatMessage({ id: 'utilityAmountPlaceholder' })}
            validate={[fieldRequired]}
          />
        </div>
        <div className="form-group">
          <label htmlFor="currency">{intl.formatMessage({ id: 'currency' })}</label>
          <Field
            name="currency"
            id="currency"
            component={requiredSelect}
            className="form-control"
            validate={[fieldRequired]}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </Field>
        </div>
      </div>
    </>
  );
};

UtilityEditForm.propTypes = {
  change: PropTypes.func.isRequired,
  utilityUnits: PropTypes.shape({}).isRequired,
  utilityType: PropTypes.number,
  activeUtilities: PropTypes.shape([]).isRequired,
  currentUtility: PropTypes.string.isRequired,
  intl: PropTypes.shape({}).isRequired,
};

const mapStateToProps = (state) => {
  const utilityType = formValueSelector('utilities')(state, 'type');
  const activeUtilities = selectActiveUtilities(state);
  const currentUtility = selectCurrentUtility(state);
  return {
    utilityType,
    activeUtilities,
    currentUtility,
  };
};

export default compose(
  injectIntl,
  connect(mapStateToProps),
  reduxForm({ form: 'utilities' })
)(UtilityEditForm);
